import { withErrorBoundary } from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'urql';
import { assets } from '../../../assets';
import DefaultScreen from '../../../components/containers/default-screen';
import { Fallback } from '../../../components/utils/error-boundary';
import { GraphqlOperations } from '../../../logic/graphql/operations';
import { Query } from '../../../logic/graphql/types/types';
import ExchangeOrders from '../../../logic/redux/exchange-orders';
import { Paths } from '../../../navigation/paths';
import NewExchangeOrderForm from './step-1-form/new-exchange-order-form';
import NewExchangeOrderDetails from './step-2-details/new-exchange-order-details';
import NewExchangeOrderConfirmation from './step-3-confirmation/new-exchange-order-confirmation';
import { useIntl } from 'react-intl';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let unregisterHistoryBlock: any;

const NewExchangeOrder = (): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();

  const exchangeOrder = useSelector(ExchangeOrders.select.selectEditedExchangeOrder);

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [token, setToken] = useState<string>('');

  const getUserNeedTransactionSecondConfirmationQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.getUserNeedTransactionSecondConfirmation.queryString,
  })[0];

  // navigation blocking: https://medium.com/@jozollo/blocking-navigation-with-react-router-v4-a3f2e359d096,
  // https://github.com/szymonkaliski/react-window-mixins/issues/2

  useEffect(() => {
    return () => {
      dispatch(ExchangeOrders.actions.setEditedExchangeOrder(undefined));
      dispatch(ExchangeOrders.actions.setInvestableAccountBalance(undefined));
      dispatch(ExchangeOrders.actions.setInvestableSecurityAmount(undefined));
      dispatch(ExchangeOrders.actions.setShouldValidate(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // blocking browser navigation
    const handler = (event: BeforeUnloadEvent): string | null => {
      const confirmationMessage = 'Biztosan bezárod';
      (event || window.event).returnValue = confirmationMessage; //Gecko + IE
      return confirmationMessage; //Gecko + Webkit, Safari, Chrome etc.
    };
    window.addEventListener('beforeunload', handler);

    return () => {
      window.removeEventListener('beforeunload', handler);
      if (unregisterHistoryBlock) {
        unregisterHistoryBlock();
        unregisterHistoryBlock = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (exchangeOrder && !unregisterHistoryBlock) {
      // blocking router
      unregisterHistoryBlock = history.block(() => {
        return 'Biztosan bezárod?';
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeOrder]);

  const handleBackClick = (): void => {
    history.goBack();
  };

  const handleSuccesfulConfirmation = (): void => {
    if (unregisterHistoryBlock) {
      unregisterHistoryBlock();
      unregisterHistoryBlock = null;
    }
    dispatch(ExchangeOrders.actions.setEditedExchangeOrder(undefined));
    dispatch(ExchangeOrders.actions.setInvestableAccountBalance(undefined));
    dispatch(ExchangeOrders.actions.setInvestableSecurityAmount(undefined));
    dispatch(ExchangeOrders.actions.setShouldValidate(false));
    history.push(Paths.exchange_orders);
  };

  const renderCurrentStepContent = (): JSX.Element | null => {
    switch (currentStep) {
      case 0:
        return (
          <NewExchangeOrderForm
            onFinishedEditing={() => {
              setCurrentStep(1);
            }}
          />
        );
      case 1:
        return (
          <NewExchangeOrderDetails
            onCreationSuccess={(t) => {
              setToken(t);
              setCurrentStep(2);
            }}
            onBackClick={() => setCurrentStep(0)}
          />
        );
      case 2:
        return (
          <NewExchangeOrderConfirmation
            onSuccess={handleSuccesfulConfirmation}
            onBackClick={() => setCurrentStep(1)}
            needsConfirmation={
              getUserNeedTransactionSecondConfirmationQueryResult.data?.getUser
                ?.need_transaction_second_confirmation
            }
            token={token}
          />
        );
      default:
        return null;
    }
  };

  return (
    <DefaultScreen
      background={assets.images.exchange_order_bg}
      title={intl.formatMessage({ id: 'exchangeOrderFormPage.title' })}
      steps={[
        intl.formatMessage({ id: 'exchangeOrderFormPage.stepData.title' }),
        intl.formatMessage({ id: 'exchangeOrderFormPage.stepOverview.title' }),
        intl.formatMessage({ id: 'exchangeOrderFormPage.stepConfirmation.title' }),
      ]}
      currentStep={currentStep}
      onBackClick={handleBackClick}
    >
      {renderCurrentStepContent()}
    </DefaultScreen>
  );
};

export default withErrorBoundary(NewExchangeOrder, { fallback: Fallback });
